<script setup lang="ts">
import { getRewardSummaryDetail, getRewardTypeIcon, getRewardTypeLabel } from '@/components/rewards/utils/rewardHelpers'
import { getClaimLimitSummary } from '@/components/rewards/utils/offerRewardHydration'

import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'
import type { LoyaltyReward } from '@/types/types'

type OfferStatus = 'active' | 'scheduled' | 'expired'

const props = defineProps<{
  offerName: string
  selectedReward: LoyaltyReward | null
  claimLimitQuantity?: number | null
  claimLimitDays?: number | null
  audienceSummary?: string
  scheduleSummary?: string
  channelsSummary?: string
  storesSummary?: string
  isPromotionalCodeRequired?: boolean
  status?: OfferStatus
}>()

const { t } = useI18n()

const rewardTypeLabel = computed(() =>
  props.selectedReward?.rewardType ? getRewardTypeLabel(props.selectedReward.rewardType, t) : ''
)

const rewardTypeIcon = computed(() =>
  props.selectedReward?.rewardType ? getRewardTypeIcon(props.selectedReward.rewardType) : 'IconoirGift'
)

const rewardSummaryItems = computed(() => (props.selectedReward ? getRewardSummaryDetail(props.selectedReward, t) : []))

const claimLimitSummary = computed(() =>
  getClaimLimitSummary(
    { claimLimitQuantity: props.claimLimitQuantity ?? null, claimLimitDays: props.claimLimitDays ?? null },
    t
  )
)

const promotionalCodeSummary = computed(() =>
  props.isPromotionalCodeRequired ? t('pages.offer.create.summary.promotionalCodeRequired') : ''
)

const statusChipColor: Record<OfferStatus, ChipColor> = {
  active: 'green',
  scheduled: 'yellow',
  expired: 'red'
}
</script>

<template>
  <Card class="overflow-hidden">
    <CardHeader class="flex flex-row items-center justify-between">
      <CardTitle>{{ t('pages.offer.create.summary.title') }}</CardTitle>
      <BaseChip v-if="status" :color="statusChipColor[status]" :text="t(`pages.offer.list.status.${status}`)" />
    </CardHeader>

    <CardContent class="space-y-5">
      <div class="rounded-xl bg-muted p-4">
        <p class="text-sm font-semibold leading-snug text-foreground">
          <span class="font-normal text-muted-foreground"> {{ t('pages.offer.create.summary.nameLabel') }}&nbsp; </span
          >{{ offerName || '—' }}
        </p>

        <div
          v-if="
            audienceSummary ||
            scheduleSummary ||
            channelsSummary ||
            storesSummary ||
            claimLimitSummary ||
            promotionalCodeSummary
          "
          class="mt-3 space-y-2 border-t border-border/80 pt-3"
        >
          <div v-if="audienceSummary" class="flex items-start gap-2.5">
            <BaseIcon name="IconoirCommunity" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary">{{ audienceSummary }}</p>
          </div>
          <div v-if="scheduleSummary" class="flex items-start gap-2.5">
            <BaseIcon name="IconoirCalendar" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary">{{ scheduleSummary }}</p>
          </div>
          <div v-if="claimLimitSummary" class="flex items-start gap-2.5">
            <BaseIcon :name="claimLimitSummary.icon as Icons" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary">{{ claimLimitSummary.text }}</p>
          </div>
          <div v-if="channelsSummary" class="flex items-start gap-2.5">
            <BaseIcon name="HeroDevicePhoneMobileIconOutline" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary">{{ channelsSummary }}</p>
          </div>
          <div v-if="storesSummary" class="flex items-start gap-2.5">
            <BaseIcon name="IconoirShop" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary">{{ storesSummary }}</p>
          </div>
          <div v-if="promotionalCodeSummary" class="flex items-start gap-2.5">
            <BaseIcon name="IconoirBarCode" class="mt-px h-4 w-4 shrink-0 text-muted-foreground" />
            <p class="text-xs leading-relaxed text-foreground-secondary dark:text-muted-foreground">
              {{ promotionalCodeSummary }}
            </p>
          </div>
        </div>
      </div>

      <div v-if="selectedReward" class="rounded-xl bg-muted">
        <div class="flex items-center gap-3 p-4">
          <img
            v-if="selectedReward.pictureUrl"
            :src="selectedReward.pictureUrl"
            :alt="selectedReward.name"
            class="h-11 w-11 shrink-0 rounded-lg object-cover"
          />
          <div v-else class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <BaseIcon name="IconoirGift" class="h-5 w-5 text-primary" />
          </div>
          <div class="min-w-0">
            <p class="text-sm font-medium leading-snug text-foreground">
              {{ selectedReward.name }}
            </p>
            <BaseChip class="mt-1" :icon="rewardTypeIcon" :text="rewardTypeLabel" />
          </div>
        </div>

        <div v-if="rewardSummaryItems.length" class="mt-3 space-y-2 border-t border-border/80 px-4 pb-4 pt-3">
          <p class="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('pages.offer.create.summary.detailTitle') }}
          </p>
          <ul class="space-y-2">
            <li
              v-for="item in rewardSummaryItems"
              :key="`${item.icon}-${item.text}`"
              class="flex items-start gap-2.5 text-xs leading-relaxed text-foreground-secondary"
            >
              <BaseIcon :name="item.icon as Icons" class="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span>
                {{ item.text }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-else
        class="flex flex-col items-center gap-3 rounded-2xl border border-border border-dashed bg-muted px-5 py-7 text-center"
      >
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-stone-200">
          <BaseIcon name="IconoirGift" class="h-5 w-5 text-muted-foreground" />
        </div>
        <div class="space-y-1 text-center">
          <p class="text-sm font-medium text-foreground">
            {{ t('pages.offer.create.summary.noRewardSelected') }}
          </p>
          <p class="text-xs text-muted-foreground">
            {{ t('pages.offer.create.summary.rewardRequiredHint') }}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
</template>
